import type {
  PipelineStage,
  Framework,
  ExpiryItem,
  ActivityItem,
  HeatmapItem,
} from "./dashboard.types";

// ── KPI Types & Mock Data ──────────────────────────────────────────

export type TimeRange = "weekly" | "monthly" | "yearly";

export type KpiId =
  | "activeAudits"
  | "completedAudits"
  | "openFindings"
  | "missingEvidence"
  | "fixRate"
  | "upcomingDeadlines";

export interface KpiData {
  id: KpiId;
  value: number;
  unit?: string;
  trend?: number;
  invertTrend?: boolean;
}

export const KPI_MOCK: Record<TimeRange, KpiData[]> = {
  weekly: [
    { id: "activeAudits", value: 12, trend: 3 },
    { id: "completedAudits", value: 2, trend: 1 },
    { id: "openFindings", value: 23, trend: -5, invertTrend: true },
    { id: "missingEvidence", value: 7, trend: 1, invertTrend: true },
    { id: "fixRate", value: 12, unit: "Tage", trend: -2 },
    { id: "upcomingDeadlines", value: 3 },
  ],
  monthly: [
    { id: "activeAudits", value: 12, trend: 3 },
    { id: "completedAudits", value: 8, trend: 2 },
    { id: "openFindings", value: 23, trend: -5, invertTrend: true },
    { id: "missingEvidence", value: 7, trend: 1, invertTrend: true },
    { id: "fixRate", value: 12, unit: "Tage", trend: -2 },
    { id: "upcomingDeadlines", value: 3 },
  ],
  yearly: [
    { id: "activeAudits", value: 12, trend: 8 },
    { id: "completedAudits", value: 47, trend: 12 },
    { id: "openFindings", value: 23, trend: -14, invertTrend: true },
    { id: "missingEvidence", value: 7, trend: -3, invertTrend: true },
    { id: "fixRate", value: 9, unit: "Tage", trend: -5 },
    { id: "upcomingDeadlines", value: 3 },
  ],
};

export const DEFAULT_VISIBLE_KPIS: KpiId[] = [
  "activeAudits",
  "completedAudits",
  "openFindings",
];

export const ALL_KPI_IDS: KpiId[] = [
  "activeAudits",
  "completedAudits",
  "openFindings",
  "missingEvidence",
  "fixRate",
  "upcomingDeadlines",
];

export const pipeline: PipelineStage[] = [
  {
    status: "Geplant",
    count: 5,
    color: "#94a3b8",
    audits: [
      {
        name: "ISO 27001 Re-Zertifizierung",
        client: "TechCorp GmbH",
        due: "15.04.2026",
        fw: "ISO 27001",
      },
      {
        name: "NIS 2 Gap Analysis",
        client: "FinServ AG",
        due: "01.05.2026",
        fw: "NIS 2",
      },
      {
        name: "SOC 2 Type II",
        client: "CloudBase Inc.",
        due: "20.05.2026",
        fw: "SOC 2",
      },
      {
        name: "TISAX Assessment",
        client: "AutoParts GmbH",
        due: "10.06.2026",
        fw: "TISAX",
      },
      {
        name: "EU AI Act Readiness",
        client: "AI Solutions SE",
        due: "30.06.2026",
        fw: "EU AI Act",
      },
    ],
  },
  {
    status: "In Vorbereitung",
    count: 4,
    color: "#60a5fa",
    audits: [
      {
        name: "ISO 27001 Surveillance",
        client: "DataHub GmbH",
        due: "28.02.2026",
        fw: "ISO 27001",
        p: 65,
      },
      {
        name: "SOC 2 Type I",
        client: "SaaSify Ltd.",
        due: "10.03.2026",
        fw: "SOC 2",
        p: 45,
      },
      {
        name: "TISAX VDA ISA",
        client: "DriveLogic AG",
        due: "15.03.2026",
        fw: "TISAX",
        p: 70,
      },
      {
        name: "NIS 2 Compliance Check",
        client: "EnergiePlus SE",
        due: "20.03.2026",
        fw: "NIS 2",
        p: 30,
      },
    ],
  },
  {
    status: "Laufend",
    count: 3,
    color: "#a78bfa",
    audits: [
      {
        name: "ISO 27001 Zertifizierung",
        client: "MedTech Solutions",
        due: "20.02.2026",
        fw: "ISO 27001",
        p: 82,
      },
      {
        name: "SOC 2 + HIPAA",
        client: "HealthCloud Inc.",
        due: "05.03.2026",
        fw: "SOC 2",
        p: 60,
      },
      {
        name: "EU AI Act Assessment",
        client: "SmartBot AG",
        due: "01.03.2026",
        fw: "EU AI Act",
        p: 45,
      },
    ],
  },
  {
    status: "Abgeschlossen",
    count: 8,
    color: "#34d399",
    audits: [
      {
        name: "ISO 27001 Re-Zert.",
        client: "SecureNet GmbH",
        due: "15.01.2026",
        fw: "ISO 27001",
        p: 100,
      },
      {
        name: "TISAX Label",
        client: "AutoVision AG",
        due: "20.01.2026",
        fw: "TISAX",
        p: 100,
      },
    ],
  },
  {
    status: "Nachverfolgung",
    count: 2,
    color: "#fbbf24",
    audits: [
      {
        name: "SOC 2 Remediation",
        client: "PayFlow Ltd.",
        due: "10.02.2026",
        fw: "SOC 2",
        p: 90,
      },
      {
        name: "ISO Maßnahmen-Check",
        client: "LogiSafe GmbH",
        due: "28.02.2026",
        fw: "ISO 27001",
        p: 75,
      },
    ],
  },
];

// Change slice to test different tiers: frameworks.slice(0, 2) for detail view
export const frameworks: Framework[] = [
  {
    name: "ISO 27001",
    cov: 92,
    ctrl: 114,
    met: 105,
    color: "#3b82f6",
    docCov: 87,
    openProcesses: 3,
    due: "15.04.2026",
    trigger: "Annual Surveillance",
    auditSlug: "jh74cbsqjjf85pxkhx2vgcsvgx827h02",
  },
  {
    name: "SOC 2",
    cov: 78,
    ctrl: 64,
    met: 50,
    color: "#8b5cf6",
    docCov: 71,
    openProcesses: 5,
    due: "20.05.2026",
    trigger: "Annual Certification",
    auditSlug: "jh74y1cpd68jwgbnfv4j0n0n8n826cm0",
  },
  {
    name: "TISAX",
    cov: 85,
    ctrl: 42,
    met: 36,
    color: "#06b6d4",
    docCov: 79,
    openProcesses: 2,
    due: "30.04.2026",
    trigger: "Initial Assessment",
  },
  {
    name: "NIS 2",
    cov: 67,
    ctrl: 38,
    met: 25,
    color: "#f59e0b",
    docCov: 58,
    openProcesses: 7,
    due: "10.06.2026",
    trigger: "Regulatory Mandate",
  },
  {
    name: "EU AI Act",
    cov: 54,
    ctrl: 28,
    met: 15,
    color: "#ef4444",
    docCov: 42,
    openProcesses: 9,
    due: "01.07.2026",
    trigger: "New Regulation",
  },
  {
    name: "NIST CSF",
    cov: 71,
    ctrl: 108,
    met: 77,
    color: "#10b981",
    docCov: 65,
    openProcesses: 4,
    due: "15.05.2026",
    trigger: "Gap Analysis",
    auditSlug: "jh741myw7a9z8h6jtqkwx9n1fx826ts6",
  },
];

export const expiry: ExpiryItem[] = [
  {
    name: "SSL-Zertifikat Produktion",
    version: "v1.0",
    exp: "18.02.2026",
    days: 4,
    tags: ["ISO 27001", "Infrastructure"],
    documentId: "jh7ssl0001",
  },
  {
    name: "NDA – Partner XY GmbH",
    version: "v2.1",
    exp: "28.02.2026",
    days: 14,
    tags: ["Compliance", "Legal"],
    documentId: "jh7nda0002",
  },
  {
    name: "Penetrationstest Report",
    version: "v1.0",
    exp: "15.03.2026",
    days: 29,
    tags: ["ISO 27001", "Evidence"],
    documentId: "jh7pen0003",
  },
  {
    name: "DSFA Aktualisierung",
    version: "v3.0",
    exp: "20.03.2026",
    days: 34,
    tags: ["GDPR", "Privacy"],
    documentId: "jh7dsf0004",
  },
  {
    name: "ISMS Schulung Team Dev",
    version: "v1.2",
    exp: "01.04.2026",
    days: 46,
    tags: ["ISO 27001", "Training"],
    documentId: "jh7ism0005",
  },
  {
    name: "ISO 27001 Zertifikat",
    version: "v2.0",
    exp: "30.06.2026",
    days: 136,
    tags: ["ISO 27001", "Compliance"],
    documentId: "jh7iso0006",
  },
].sort((a, b) => a.days - b.days);

export const activity: ActivityItem[] = [
  {
    user: "Anna M.",
    av: "AM",
    action: "hat Evidenz hochgeladen",
    target: "ISO 27001 – A.8.1",
    time: "vor 12 Min.",
    bg: "#3b82f6",
  },
  {
    user: "Tobias K.",
    av: "TK",
    action: "Finding geschlossen",
    target: "SOC 2 – CC6.1",
    time: "vor 34 Min.",
    bg: "#10b981",
  },
  {
    user: "Laura S.",
    av: "LS",
    action: "Kommentar hinzugefügt",
    target: "TISAX – ISA 5.2.6",
    time: "vor 1 Std.",
    bg: "#8b5cf6",
  },
  {
    user: "Max B.",
    av: "MB",
    action: "Audit gestartet",
    target: "NIS 2 Gap Analysis",
    time: "vor 2 Std.",
    bg: "#f59e0b",
  },
  {
    user: "Sarah H.",
    av: "SH",
    action: "Maßnahme zugewiesen",
    target: "EU AI Act – Art. 9",
    time: "vor 3 Std.",
    bg: "#f43f5e",
  },
];

export const RISK_LEVELS = [
  { level: "Niedrig", color: "#dcfce7", order: 0 },
  { level: "Mittel", color: "#fde68a", order: 1 },
  { level: "Hoch", color: "#fb923c", order: 2 },
  { level: "Kritisch", color: "#ef4444", order: 3 },
] as const;

export const heatmap: HeatmapItem[] = [
  {
    cat: "Zugangskontrolle",
    r: 3,
    i: 4,
    gaps: 2,
    level: "Kritisch",
    details: [
      {
        id: "H-001",
        title: "Admin-Zugänge ohne MFA",
        control: "A.5.17",
        status: "Offen",
      },
      {
        id: "H-002",
        title: "Passwortrichtlinie nicht durchgesetzt",
        control: "CC6.1",
        status: "In Bearbeitung",
      },
    ],
  },
  {
    cat: "Datenschutz",
    r: 4,
    i: 5,
    gaps: 3,
    level: "Kritisch",
    details: [
      {
        id: "H-003",
        title: "DSFA nicht aktualisiert",
        control: "A.8.24",
        status: "Geplant",
      },
      {
        id: "H-004",
        title: "Vertragsverarbeitung unvollständig dokumentiert",
        control: "Art. 28 DSGVO",
        status: "Offen",
      },
    ],
  },
  {
    cat: "Netzwerksicherheit",
    r: 2,
    i: 4,
    gaps: 1,
    level: "Hoch",
    details: [
      {
        id: "H-005",
        title: "Netzwerksegmentierung unvollständig",
        control: "ISA 5.2.6",
        status: "In Bearbeitung",
      },
    ],
  },
  {
    cat: "Physische Sicherheit",
    r: 1,
    i: 3,
    gaps: 0,
    level: "Niedrig",
    details: [
      {
        id: "H-006",
        title: "Besuchermanagement nicht digital",
        control: "A.7.7",
        status: "Geplant",
      },
    ],
  },
  {
    cat: "Incident Response",
    r: 3,
    i: 5,
    gaps: 2,
    level: "Kritisch",
    details: [
      {
        id: "H-007",
        title: "Incident-Playbook veraltet",
        control: "A.5.24",
        status: "Offen",
      },
      {
        id: "H-008",
        title: "Kontaktdaten Notfallplan unvollständig",
        control: "A.5.30",
        status: "In Bearbeitung",
      },
    ],
  },
  {
    cat: "Business Continuity",
    r: 4,
    i: 5,
    gaps: 4,
    level: "Kritisch",
    details: [
      {
        id: "H-009",
        title: "BCP nicht getestet (>12 Monate)",
        control: "A.5.29",
        status: "Offen",
      },
      {
        id: "H-010",
        title: "Backup-Wiederherstellung nicht validiert",
        control: "A.8.13",
        status: "Geplant",
      },
    ],
  },
  {
    cat: "Lieferanten-Mgmt",
    r: 3,
    i: 3,
    gaps: 2,
    level: "Mittel",
    details: [
      {
        id: "H-011",
        title: "Lieferantenbewertung nicht aktualisiert",
        control: "A.5.19",
        status: "In Bearbeitung",
      },
    ],
  },
  {
    cat: "Kryptographie",
    r: 2,
    i: 4,
    gaps: 1,
    level: "Hoch",
    details: [
      {
        id: "H-012",
        title: "Veraltete TLS-Konfiguration",
        control: "Art. 21(2)(e)",
        status: "Offen",
      },
    ],
  },
  {
    cat: "HR-Sicherheit",
    r: 1,
    i: 2,
    gaps: 0,
    level: "Niedrig",
    details: [
      {
        id: "H-013",
        title: "Schulungsnachweis fehlt für 3 Mitarbeiter",
        control: "A.6.3",
        status: "Offen",
      },
    ],
  },
];

export const radarLabels = [
  "Zugangskontrolle",
  "Datenschutz",
  "Netzwerk",
  "Physische Sicherheit",
  "Incident Response",
  "Business Continuity",
];

export const radarData = [88, 65, 92, 95, 72, 58];
